/* ==========================================================================
   topupReturn

   When OPay sends the person back to the app after paying (or cancelling), the
   address carries ?topup=<reference>. This shows a small "confirming your
   payment" box, asks the server whether OPay confirmed it (every few seconds,
   for up to a minute), and then tells the person the outcome. The wallet is
   credited by the server, never by this code.
   ========================================================================== */

import { walletService } from "../services/walletService";
import { formatNaira } from "./format";

const POLL_MS = 3000;
const MAX_TRIES = 20;

function overlay(): { panel: HTMLElement; close: () => void } {
  const wrap = document.createElement("div");
  wrap.className = "topup-overlay";
  const panel = document.createElement("div");
  panel.className = "topup-panel";
  wrap.appendChild(panel);
  document.body.appendChild(wrap);
  return { panel, close: () => wrap.remove() };
}

function render(panel: HTMLElement, title: string, text: string, button?: { label: string; onClick: () => void }): void {
  panel.innerHTML = "";
  const h = document.createElement("h2");
  h.textContent = title;
  const p = document.createElement("p");
  p.textContent = text;
  panel.append(h, p);
  if (button) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "btn-primary topup-btn";
    b.textContent = button.label;
    b.addEventListener("click", button.onClick);
    panel.appendChild(b);
  }
}

export async function handleTopupReturn(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const reference = params.get("topup");
  if (!reference) return;
  const cancelled = params.get("cancelled") === "1";
  // Take the reference out of the address bar so a refresh doesn't repeat this.
  window.history.replaceState(null, "", window.location.pathname);

  const { panel, close } = overlay();
  const finish = () => { close(); window.location.reload(); };
  render(panel, "Confirming your payment…", "Please wait a moment. Don't close this page.");

  for (let i = 0; i < MAX_TRIES; i++) {
    const result = await walletService.checkTopup(reference);
    if (result.status === "success") {
      const amount = result.amountKobo ? ` ₦${formatNaira(result.amountKobo)}` : "";
      render(panel, "Payment received", `${amount ? amount.trim() + " has" : "Your money has"} been added to your wallet.`, { label: "Done", onClick: finish });
      return;
    }
    if (result.status === "failed") {
      render(panel, cancelled ? "Payment cancelled" : "Payment not completed", "No money was added to your wallet. You can try again.", { label: "Close", onClick: finish });
      return;
    }
    if (result.status === "review") {
      render(panel, "Payment under review", "We received your payment but need to check it before adding it. Please contact your estate admin or PropertyVest support with your payment reference: " + reference, { label: "Close", onClick: finish });
      return;
    }
    if (cancelled && i >= 2) {
      render(panel, "Payment cancelled", "You cancelled this payment, so no money was taken.", { label: "Close", onClick: finish });
      return;
    }
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
  render(panel, "Still waiting for confirmation", "We haven't heard back from the bank yet. If money left your account, it will appear in your wallet shortly. Please check again in a few minutes. Reference: " + reference, { label: "Close", onClick: finish });
}
